import { type Dispatch, type FC, type SetStateAction, useMemo } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalSidebar } from '@/components/commons/gallery-modal-sidebar'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { GalleryFlags } from '@/features/photoset/components/gallery-photoset-modal/gallery-flags/gallery-flags'
import { GalleryMetadata } from '@/features/photoset/components/gallery-photoset-modal/gallery-metadata/gallery-metadata'
import { useGalleryCurationShortcuts } from '@/features/photoset/hooks/use-gallery-curation-shortcuts'
import { usePhotosetStore } from '@/features/photoset/store/store'
import { type Image, StepKey } from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

interface GalleryCurationModal {
	selectableImages: Omit<Image, 'space'>[]
	currentDetailImageIndex: number
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
	onClose: () => void
	currentSpaceId?: number // This is optional to enable the use of component in Curation V1
}

export const GalleryCurationModal: FC<GalleryCurationModal> = ({
	selectableImages,
	currentDetailImageIndex,
	setCurrentDetailImageIndex,
	onClose,
	currentSpaceId
}) => {
	const { t } = useTranslation()
	const photosetSpaceImageSelectionToggle = usePhotosetStore(
		(state) => state.photosetSpaceImageSelectionToggle
	)
	const toggleSelectImage = usePhotosetStore(
		(state) => state.toggleSelectImage
	)
	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const photosetSpaces = usePhotosetStore((state) => state.photosetSpaces)

	const currentImage = useMemo(
		() => selectableImages[currentDetailImageIndex],
		[currentDetailImageIndex, selectableImages]
	)

	const currentSpaceIndex = useMemo<number | null>(
		() =>
			currentSpaceId !== undefined && currentSpaceId > 0
				? photosetSpaces.findIndex(
						(space) => space.id === currentSpaceId
					)
				: null,
		[currentSpaceId, photosetSpaces]
	)

	useGalleryCurationShortcuts({
		currentDetailImageIndex,
		currentSpaceIndex,
		setCurrentDetailImageIndex,
		currentImage,
		selectableImages,
		onClose
	})

	return (
		<GalleryModal
			className='bg-neutral-white'
			currentImage={currentImage}
			headerSlot={
				<GalleryModalHeader
					currentImage={currentImage}
					endSlot={
						currentImage.thumbnails?.xs && (
							<ButtonLegacy
								onClick={(e) => {
									currentSpaceIndex === null ||
									currentSpaceIndex < 0
										? toggleSelectImage(currentImage.id)
										: photosetSpaceImageSelectionToggle(
												currentSpaceIndex,
												currentImage.id
											)
									e.currentTarget.blur()
								}}
								tabIndex={-1}
								type='button'
								variant='secondary'
							>
								<Checkbox
									checked={
										((currentSpaceIndex === null ||
											currentSpaceIndex < 0) &&
											selectedImages.includes(
												currentImage.id
											)) ||
										currentImage.is_final_select
									}
									readOnly
									tabIndex={-1}
								/>
								{t('common:select_space')}
							</ButtonLegacy>
						)
					}
					imagesCount={selectableImages.length}
					onClose={onClose}
				/>
			}
			onClose={onClose}
			sidebarSlot={
				<GalleryModalSidebar
					content={
						<>
							<GalleryMetadata
								imageMetadata={currentImage.metadata}
							/>
							<GalleryFlags
								image={currentImage}
								photosetSpaceIds={
									currentSpaceId ? [currentSpaceId] : []
								}
								// This ternary condition is necessary for Curation V1 to function properly
								stepId={StepKey.curation}
							/>
						</>
					}
					title={t('photoset:curation.sidebar_title')}
				/>
			}
			thumbnails={currentImage.thumbnails}
			thumbnailsSlot={
				<GalleryModalThumbnails
					currentImageId={currentImage.id}
					images={selectableImages}
					onSelect={(_, index) => {
						setCurrentDetailImageIndex(index)
					}}
					thumbnails={'thumbnails'}
				/>
			}
		/>
	)
}
